/* Settings ▸ Permit Library ▸ Permit Row Colors — the EXAMPLE PERMIT the Preview colours against.
 *
 * A preview-only aid: nothing here is saved and nothing touches a real permit or a real result.
 * The values only become the `PermitLimit[]` + row MDL the shared engine (`permitRowRule`,
 * permitLimit.ts) already takes, so the preview is coloured by EXACTLY the logic the result forms use.
 *
 * DRIVEN BY THE RULES, NOT FIXED (owner request 2026-10-23): the limit boxes are the limit types the
 * previewed rule(s) actually name — the rule ticked in the grid, or the rule being edited — so a rule
 * on "MCL, SMCL" shows MCL and SMCL boxes, and the example rows are built around those values
 * (`previewRows`, PermitRowColorDetail.tsx). There is no fixed MCL / AL / MRL / MDL set any more.
 * A box starts at a sensible default for its type and keeps what the user typed while the page is
 * open; Reset forgets the typed values.
 *
 * Validation (RTCFR 2026-10-23): a limit box may be EMPTY — that is a limit the permit does not
 * have, skipped exactly as the engine skips a real missing limit, never read as 0. Zero is a valid
 * value. Anything that is not a plain non-negative number is an error, and an invalid box is
 * treated as empty (so a half-typed "abc" never colours the preview with a guess). The tried result
 * may be a plain number or a qualified value `<x` / `>x` — the same forms the engine understands. */
import type { PermitLimit } from '../../analysis/permitLimit';
import type { RowColorRule } from '../../analysis/permitRowColorState';

export const EXAMPLE_UNITS = 'mg/L';

/** What the user TYPED, per limit-type name (a box not typed in shows its default). Kept as
 *  text so a half-typed number ("0.") stays in its box. */
export type ExamplePermit = Record<string, string>;
export const DEFAULT_EXAMPLE: ExamplePermit = {};
export const DEFAULT_TRY_RESULT = '3.2';

/** One example-limit box: the limit type's name, and the operator a permit gives it. */
export interface ExampleField { key: string; label: string; op?: string }

/** Where a box starts, so the preview shows something sensible before anyone types. The common
 *  drinking-water types get a realistic ladder (MDL < MRL < AL < SMAL < SMCL < MCL); any other
 *  type a lab configures starts at 10. These are preview starting values only — never a rule. */
const DEFAULT_VALUES: Record<string, string> = {
  MDL: '0.5', MRL: '1', AL: '2', SMAL: '2.5', SMCL: '3', MCL: '5',
};
const KNOWN_ORDER = ['MDL', 'MRL', 'AL', 'SMAL', 'SMCL', 'MCL'];
const OTHER_DEFAULT = '10';

const norm = (name: string) => name.trim().toUpperCase();

export function defaultLimitValue(key: string): string {
  return DEFAULT_VALUES[norm(key)] ?? OTHER_DEFAULT;
}

/** The limit types the given rules name, once each (case-insensitive), MDL / MRL / AL / SMAL /
 *  SMCL / MCL first in that order, then any other type alphabetically. A limit type deleted since
 *  the rule was saved (no name) is left out. */
export function exampleFieldsFor(rules: RowColorRule[]): ExampleField[] {
  const names = new Map<string, string>();
  for (const r of rules) {
    for (const c of r.conditions) {
      for (const l of c.limits) {
        const name = l.id === 'MDL' ? 'MDL' : l.name?.trim();
        if (name && !names.has(norm(name))) names.set(norm(name), name);
      }
    }
  }
  const rank = (k: string) => { const i = KNOWN_ORDER.indexOf(k); return i < 0 ? KNOWN_ORDER.length : i; };
  return [...names.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([k, label]) => ({ key: k, label, op: k === 'MDL' ? undefined : '≤' }));
}

/** The text a box shows: what was typed, else its default. */
export function exampleText(example: ExamplePermit, key: string): string {
  return Object.prototype.hasOwnProperty.call(example, key) ? example[key]! : defaultLimitValue(key);
}

/** A plain non-negative decimal: 5 · 0 · 0.5 · .5 · 5. (the last while it is still being typed). */
const PLAIN = /^(\d+(\.\d*)?|\.\d+)$/;
/** A result: a plain number, or one qualified with < / > (the engine's censored forms). */
const RESULT = /^([<>])?\s*(\d+(\.\d*)?|\.\d+)$/;

/** The box's number, or null when it is empty or not a valid number (both mean "no such limit"). */
export function limitValue(text: string): number | null {
  const t = text.trim();
  return PLAIN.test(t) ? Number(t) : null;
}

/** The error to show under a limit box, or undefined when it is valid (empty is valid). */
export function limitError(label: string, text: string): string | undefined {
  const t = text.trim();
  if (!t || PLAIN.test(t)) return undefined;
  return t.startsWith('-')
    ? `${label} cannot be negative.`
    : `${label} must be a number, e.g. 5 or 0.5 — or leave it empty if the permit has no ${label}.`;
}

/** The error to show for the tried result, or undefined when it is valid. Empty is not an error —
 *  there is simply nothing to evaluate yet. */
export function resultError(text: string): string | undefined {
  const t = text.trim();
  if (!t || RESULT.test(t)) return undefined;
  return 'Enter a number such as 3.2, or a qualified value such as <0.5 or >6.';
}

/** The example permit as the limits a real permit sends — one per box except MDL (which is the
 *  row's own MDL). An empty or invalid box is a limit the permit does not have — left out, so the
 *  engine skips it (never treated as 0). */
export function exampleLimits(example: ExamplePermit, fields: ExampleField[]): PermitLimit[] {
  const out: PermitLimit[] = [];
  for (const f of fields) {
    if (f.key === 'MDL') continue;
    const n = limitValue(exampleText(example, f.key));
    if (n !== null) out.push({ type: f.label, operator: 'LessThanEqual', limit1: n, units: EXAMPLE_UNITS });
  }
  return out;
}

/** The example row MDL — only when the rules name MDL and its box holds a number, else null. */
export function exampleMdl(example: ExamplePermit, fields: ExampleField[]): number | null {
  return fields.some((f) => f.key === 'MDL') ? limitValue(exampleText(example, 'MDL')) : null;
}
